// Yöneticinin metin alanına yazdığı ham HTML'in çizim kuralları.
//
// Yönetici başka bir sitenin kural şablonunu (kendi <style>'ı, checkbox
// akordeonu, küçük betikler) olduğu gibi yapıştırabilir. İçerik portalın
// DOM'una basılmaz: bir sandbox iframe'inde çizilir ve iframe'e
// ``allow-same-origin`` **verilmez** — içerideki betik sitenin oturum jetonuna,
// çerezine ve DOM'una erişemez. Şablonun CSS'i siteye, sitenin CSS'i şablona
// karışmaz.
//
// Yüksekliği iframe kendisi bildirir (postMessage): origin opak olduğu için
// dışarıdan ölçülemez. Ölçü ``body``'nin kutusudur; ``display: flow-root``
// çocukların taşan kenar boşluklarını içeride tutar ve ``html`` yüksekliği
// serbest kaldığı için akordeon kapandığında çerçeve geri küçülebilir.

export const RICH_HTML_MESSAGE = 'bhs-rich-html-height';
// Küçük önizleme (kart/vitrin görsel alanı) için şablonun dolu alanı.
export const RICH_HTML_BOUNDS_MESSAGE = 'bhs-rich-html-bounds';
export const RICH_HTML_SANDBOX = [
  'allow-scripts',
  'allow-popups',
  'allow-popups-to-escape-sandbox',
  // Şablondaki "Para Yatır" gibi site içi bağlantılar yalnızca oyuncunun
  // tıklamasıyla ana sayfayı yönlendirebilir; betik kendiliğinden yapamaz.
  'allow-top-navigation-by-user-activation',
].join(' ');

export const MIN_FRAME_HEIGHT = 40;
export const MAX_FRAME_HEIGHT = 20000;

// Açılı ayraçla başlayıp harfle devam eden bir etiket ("<div", "</p>", "<br/>").
// "a < b" ya da "<3" gibi düz metinler eşleşmez — backend'deki
// services/rich_text.py ile aynı ölçü.
const TAG = /<(?:\/?[a-zA-Z][\w:-]*)(?:\s[^<>]*)?\/?>/;

/** Metin en az bir HTML etiketi taşıyor mu. */
export const looksLikeHtml = (value) => TAG.test(String(value || ''));

// Şablonlar Türkçe harfleri sıklıkla HTML varlığı olarak taşır
// (`&uuml;`, `&ccedil;`); elle yazılmış küçük bir tablo onları kaçırır ve kartta
// "g&uuml;n" görünürdü. Çözümleme tarayıcıya bırakılır: metin bir
// `<textarea>`'nın içeriği olarak ayrıştırılır (orada etiket değil **metin**
// olarak okunur) ve yalnızca `value` geri alınır — hiçbir düğüm sayfaya
// eklenmez. Tarayıcı yoksa değer olduğu gibi kalır.
const decodeEntities = (value) => {
  if (typeof document === 'undefined') return value;
  const holder = document.createElement('textarea');
  holder.innerHTML = value;
  return holder.value;
};

// Metni doğrudan basan yüzeylerin (panel liste kartı) önizlemesi: etiketler ve
// görünmeyen bloklar ayıklanır, yoksa kartta "<div class=..." ve CSS kuralları
// okunurdu. Oyuncu tarafında aynı işi backend yapar (services/rich_text.py).
export const plainExcerpt = (value, limit = 160) => {
  const stripped = String(value || '')
    .replace(/<!--[\s\S]*?-->/g, ' ')
    // "<!DOCTYPE html>" etikete benzemez; tam belge olarak yapıştırılan
    // şablonda kartın ilk kelimesi o olurdu. <head> (ve içindeki <title>)
    // görünmez: sayfa başlığı özete ikinci bir başlık olarak düşerdi.
    .replace(/<[!?][^<>]*>/g, ' ')
    .replace(/<(style|script|head|title|template|noscript)\b[^>]*>[\s\S]*?<\/\1\s*>/gi, ' ')
    .replace(/<(?:\/?[a-zA-Z][\w:-]*)(?:\s[^<>]*)?\/?>/g, ' ');
  const text = decodeEntities(stripped).replace(/\s+/g, ' ').trim();
  return text.length > limit ? `${text.slice(0, limit).trimEnd()}…` : text;
};

export const clampFrameHeight = (value) => {
  const height = Math.ceil(Number(value));
  if (!Number.isFinite(height)) return null;
  return Math.min(MAX_FRAME_HEIGHT, Math.max(MIN_FRAME_HEIGHT, height));
};

const escapeAttribute = (value) => String(value)
  .replace(/&/g, '&amp;')
  .replace(/"/g, '&quot;')
  .replace(/</g, '&lt;');

// </style> ile biten bir değer stil bloğunu kapatabilirdi; token değerleri
// renk/ölçüdür, açılı ayraç taşımaları gerekmez.
const cssValue = (value) => String(value || '').replace(/[<>]/g, '');

/**
 * Sitenin tema değişkenlerini (``--pusu-*``) iframe'e taşır: şablon
 * ``var(--pusu-gold)`` yazarak sitenin vurgu rengini kullanabilir.
 * ``applySiteTheme`` tokenları <html> üzerine inline yazdığı için oradan okunur.
 */
export const readThemeTokens = (doc = typeof document !== 'undefined' ? document : null) => {
  if (!doc?.documentElement) return {};
  const style = doc.documentElement.style;
  const tokens = {};
  for (let index = 0; index < style.length; index += 1) {
    const name = style[index];
    if (name.startsWith('--pusu-')) tokens[name] = style.getPropertyValue(name).trim();
  }
  return tokens;
};

const RESIZE_SCRIPT = `(function(){
var type=${JSON.stringify(RICH_HTML_MESSAGE)},last=-1;
function send(){var b=document.body;if(!b)return;
var h=Math.ceil(b.getBoundingClientRect().height);
if(h===last)return;last=h;parent.postMessage({type:type,height:h},'*');}
if(window.ResizeObserver){new ResizeObserver(send).observe(document.body);}
window.addEventListener('load',send);
document.addEventListener('click',function(){setTimeout(send,50);setTimeout(send,450);});
document.addEventListener('toggle',send,true);
send();})();`;

// Şablonun gerçekten dolu olan alanı. Şablonların çoğu dar bir kutuya
// (ör. max-width: 420px) tasarlanır ve geniş çerçevede solda ya da ortada
// küçük bir sütun olarak kalır; küçük önizleme bu alanı ölçüp kutuya o alanı
// sığdırır. Ölçü: kendi metni olan öğeler, görseller ve kenarlığı/zemini olan
// ama sayfa genişliğini kaplamayan kutular (tam genişlikteki sarmalayıcı
// alanı genişletmesin diye). Hiçbiri yoksa sayfanın tamamı sayılır.
const BOUNDS_SCRIPT = `(function(){
var type=${JSON.stringify(RICH_HTML_BOUNDS_MESSAGE)},last='';
var SKIP={SCRIPT:1,STYLE:1,META:1,TITLE:1,LINK:1,HEAD:1,NOSCRIPT:1,TEMPLATE:1,BR:1};
var MEDIA={IMG:1,SVG:1,VIDEO:1,CANVAS:1,INPUT:1,BUTTON:1,PICTURE:1};
function ownText(el){for(var n=el.firstChild;n;n=n.nextSibling){if(n.nodeType===3&&n.nodeValue.trim())return true;}return false;}
function painted(cs){return parseFloat(cs.borderTopWidth)>0||parseFloat(cs.borderLeftWidth)>0||(cs.backgroundColor&&cs.backgroundColor!=='rgba(0, 0, 0, 0)'&&cs.backgroundColor!=='transparent')||cs.backgroundImage!=='none';}
function send(){var b=document.body;if(!b)return;var W=document.documentElement.clientWidth,l=1e9,t=1e9,r=-1e9,btm=-1e9;
var all=b.querySelectorAll('*');for(var i=0;i<all.length&&i<4000;i++){var el=all[i];if(SKIP[el.tagName])continue;
var rc=el.getBoundingClientRect();if(rc.width<1||rc.height<1)continue;var cs=getComputedStyle(el);
if(cs.visibility==='hidden'||cs.display==='none')continue;
var take=ownText(el)||MEDIA[el.tagName]||(rc.width<W-2&&painted(cs));if(!take)continue;
if(rc.left<l)l=rc.left;if(rc.right>r)r=rc.right;if(rc.top<t)t=rc.top;if(rc.bottom>btm)btm=rc.bottom;}
if(r<=l){l=0;r=W;t=0;btm=b.getBoundingClientRect().height;}l=Math.max(0,Math.floor(l)-8);r=Math.min(W,Math.ceil(r)+8);t=Math.max(0,Math.floor(t)-8);btm=Math.ceil(btm)+8;
var key=l+','+r+','+t+','+btm;if(key===last)return;last=key;parent.postMessage({type:type,left:l,right:r,top:t,bottom:btm,width:W},'*');}
if(window.ResizeObserver){new ResizeObserver(send).observe(document.body);}
window.addEventListener('load',send);if(document.fonts&&document.fonts.ready)document.fonts.ready.then(send);
send();})();`;

/**
 * Küçük önizlemenin yerleşimi: şablonun dolu alanı (``bounds``, çerçevenin
 * sanal genişliğindeki piksel) kutunun genişliğine sığdırılır ve üstünden
 * başlatılır. İçerik kutudan kısaysa dikeyde ortalanır. Ölçü henüz yoksa
 * sanal genişliğin tamamı sığdırılır.
 */
export const thumbnailTransform = ({ box, bounds, virtualWidth }) => {
  const area = bounds || { left: 0, right: virtualWidth, top: 0, bottom: 0 };
  const contentWidth = Math.max(1, area.right - area.left);
  const scale = box.width / contentWidth;
  const contentHeight = Math.max(0, area.bottom - area.top) * scale;
  const offsetY = contentHeight > 0 && contentHeight < box.height
    ? (box.height - contentHeight) / 2
    : 0;
  const x = -area.left * scale;
  const y = offsetY - area.top * scale;
  return {
    scale,
    frameHeight: Math.ceil((box.height - offsetY) / scale + area.top),
    transform: `translate(${x}px, ${y}px) scale(${scale})`,
  };
};

/**
 * Yönetici HTML'ini iframe ``srcdoc`` belgesine sarar.
 *
 * Göreli bağlantılar sitenin köküne göre çözülsün diye ``<base>`` yazılır
 * (srcdoc'un kendi adresi ``about:srcdoc``'tur); varsayılan hedef ana
 * pencere olur, yeni sekme isteyen bağlantı ``target="_blank"`` yazar.
 */
export const buildRichHtmlDocument = (html, {
  origin = '', fontFamily = '', color = '', tokens = {}, thumbnail = false,
} = {}) => {
  const tokenCss = Object.entries(tokens)
    .map(([name, value]) => `${name}:${cssValue(value)};`)
    .join('');
  const base = origin
    ? `<base href="${escapeAttribute(`${origin.replace(/\/$/, '')}/`)}" target="_top">`
    : '<base target="_top">';
  return `<!doctype html>
<html lang="tr">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
${base}
<style>
:root{color-scheme:dark;${tokenCss}}
html{height:auto;background:transparent}
body{display:flow-root;margin:0;background:transparent;color:${cssValue(color) || '#d1d5db'};font-family:${cssValue(fontFamily) || 'system-ui,sans-serif'};font-size:14px;line-height:1.6;overflow-wrap:anywhere}
*,*::before,*::after{box-sizing:border-box}
img,video{max-width:100%;height:auto}
${thumbnail ? 'html,body{overflow:hidden!important}' : ''}
</style>
</head>
<body>
${html || ''}
<script>${thumbnail ? BOUNDS_SCRIPT : RESIZE_SCRIPT}</script>
</body>
</html>`;
};
